// @ts-strict-ignore
import {getProjectId} from 'common/report/util';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {UserPersistence} from 'common/report/persistence';
import {PerfMeasure} from 'common/report/perf-measure';
import {trackPerfDuration} from 'common/util/workflow-timing';

import {appApiUrl} from '../utils';
import {BaseClient, QueryOptions} from '../utils/base-client';

class CurrentProjectClient extends BaseClient {
  // this endpoint often blocks initial UIs because project info is so critical,
  // it warrants using synchronous localStorage caching that will return more quickly than the async Cache API typically used as the fetch cache
  public persistence: UserPersistence<TODO> | null = null;
  queryCounter = 0;

  constructor(...args) {
    super(...args);

    const userId = userCookieUtils.getUserIdentityFromCookie()?.id;
    // note: check for a valid user ID because in a http://localhost:8080 setup, getUserIdentityFromCookie() can fail to access the cookie due to cross-origin policy
    if (userId) {
      this.persistence = new UserPersistence<TODO>({
        namespace: LocalStorageKey.CurrentProjectClientCache,
        userId,
      });
    }
  }

  buildEndpoint({projectId}) {
    projectId = projectId || getProjectId();
    if (!projectId) {
      throw new Error(`Attempting to query current project when no ID is available`);
    }
    return appApiUrl(`metadata`, {projectId});
  }

  public async query(params: {projectId}, queryOpts: QueryOptions = {}): Promise<TODO> {
    this.queryCounter += 1;
    const queryMeasure = new PerfMeasure(`current-project-client-query-${this.queryCounter}`);
    const endMeasure = (cacheHit: boolean) => {
      queryMeasure.end();
      // limit amount of tracking, tracking itself is not cheap and its perf cost can add up in pathological cases
      if (this.queryCounter < 5) {
        trackPerfDuration(`CurrentProjectClient query fired`, queryMeasure, {
          useCache: !!queryOpts.fromFetchCache,
          cacheHit,
        });
      }
    };
    queryMeasure.start();

    if (this.persistence) {
      const cacheKey = (params.projectId || getProjectId()).toString();
      if (queryOpts.clearCache) {
        this.persistence.remove(cacheKey);
      }
      if (queryOpts.fromFetchCache) {
        const storedResults = this.persistence.get(cacheKey);
        endMeasure(!!storedResults);
        if (storedResults) {
          return storedResults;
        }
      }

      // override queryOpts since we're bypassing fetch-cache
      const results = await super.query(params, {
        ...queryOpts,
        fromFetchCache: false,
        updateFetchCache: false,
      });

      if (queryOpts.updateFetchCache) {
        this.persistence.set(cacheKey, results);
      }
      endMeasure(false);
      return results;
    }

    return super.query(params, queryOpts);
  }

  processResponse(response) {
    return response.results;
  }
}

export const currentProjectClient = new CurrentProjectClient();
